// FLAGS: health-data, silent-failure
// Reads and writes the clinical record of one session: theme, topics, referral decision, follow-up and
// the two free-text narratives, each saved field-by-field as the operator leaves it.
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import Spinner from "@openup/shared/components/Spinner/Spinner";
import Button from "@openup/shared/components/Button/Button";
import { useAppInsightsContext } from "@microsoft/applicationinsights-react-js";
import {
  getHelpNeededOptions,
  getFinancialWellbeingHelpNeededOptions,
  getNextStepsForSessionNotes,
  getOpenUpNetworkProviders,
  SESSION_NOTES_SECTIONS,
} from "@src/utils/constants";
import {
  getTranscriptionSummary,
  useConsultMutation,
  useConsultNotesMutation,
  useDeleteSessionNoteMutation,
} from "@src/queries/consults";
import {
  useConsultTagsQuery,
  useSessionNoteTopicsByTheme,
} from "@src/queries/tags";
import {
  getTranslatedThemeNameInSessionLanguage,
  getTranslatedTopicName,
} from "@src/utils/languageHelpers";
import {
  HELP_NEEDED_VALUES,
  PROVIDER_VALUES,
  SESSION_TYPE,
  TAG_TYPES,
  TOPIC_NAMES,
} from "@src/constants";
import AttachmentsBox from "@src/views/ClientPage/SessionNotes/components/AttachmentsBox";
import {
  ResponseConsultsConsultWithNotesResponse,
  ResponseThemeResponse,
} from "@src/api/v1/data-contracts";
import {
  Autocomplete,
  Checkbox,
  TextField,
  TextFieldProps,
} from "@mui/material";
import {
  CheckBoxOutlineBlank as CheckBoxOutlineBlankIcon,
  CheckBox as CheckBoxIcon,
} from "@mui/icons-material";
import { CircleMinus } from "lucide-react";
import EditableMarkdownSection from "@src/views/ClientPage/SessionNotes/components/EditableMarkdownSection";
import { useThemesQuery } from "@src/queries/themes";
import { MDXEditorMethods } from "@mdxeditor/editor";
import {
  withSnackbar,
  WithSnackbarProps,
} from "@src/components/SnackBarComponent";
import SessionNoteRow from "./components/SessionNoteRow";
import EditableDisplay from "./components/EditableDisplay";
import EditableSelectField from "./components/EditableSelectField";
import { EditingContext, NoteSections } from "./EditingContext";

const CLICKABLE_FIELD_CLASS =
  "w-full p-1 body1 text-indigo-800 hover:bg-slate-100 hover:cursor-pointer";

interface ConsultNotesVM {
  consultId: number;
  theme: ResponseThemeResponse | undefined;
  topics: string[] | undefined;
  reasonBook: string;
  sessionDiscussed: string;
  helpNeeded: string | null | undefined;
  provider: string | null | undefined;
  providerName: string | null | undefined;
  nextSteps: string | null | undefined;
  caOtherDescription: string;
}
const SessionNotesPaper: React.FC<
  {
    consult: ResponseConsultsConsultWithNotesResponse;
    consultIsLoading: boolean;
  } & WithSnackbarProps
> = ({ consult, consultIsLoading, snackbarShowMessage }) => {
  const { t } = useTranslation();
  const appInsights = useAppInsightsContext();

  const [isTranscriptionSummaryLoading, setIsTranscriptionSummaryLoading] =
    useState(false);
  const [selectedTopics, setSelectedTopics] = useState<string[] | undefined>();
  const [editingSection, setEditingSection] = useState<
    NoteSections | undefined
  >();
  const sessionDiscussedRef = useRef<MDXEditorMethods>(null);

  const { data: allSessionThemes } = useThemesQuery(
    consult?.consultType ? [consult.consultType] : [],
  );
  const { consultTags: consultTopics, saveTags } = useConsultTagsQuery(
    consult.user.id!,
    consult.id,
    TAG_TYPES.TOPICS,
  );
  const {
    updateConsultTheme: { mutateAsync: updateConsultTheme },
  } = useConsultMutation(consult.user.id!);
  const { data: topicTags } = useSessionNoteTopicsByTheme(
    consult.themeKey || undefined,
    { placeholderData: (prev) => prev },
  );

  const isConfidentialAdvisorSessionType = useMemo(
    () => consult.consultType === SESSION_TYPE.CONFIDENTIAL_ADVISOR,
    [consult.consultType],
  );

  const sortedTopics = useMemo(() => {
    if (!topicTags) return [];
    return [...topicTags].toSorted((a: string, b: string) => {
      // to move "ConfidentialAdvisorOther" to the end of the list, as it's a catch-all topic that should be last regardless of alphabetical order
      const aIsOther = a === TOPIC_NAMES.CONFIDENTIAL_ADVISOR_OTHER;
      const bIsOther = b === TOPIC_NAMES.CONFIDENTIAL_ADVISOR_OTHER;
      if (aIsOther !== bIsOther) return aIsOther ? 1 : -1;

      const translatedTopicA =
        getTranslatedTopicName(a, consult.consultLanguage) ?? "";
      const translatedTopicB =
        getTranslatedTopicName(b, consult.consultLanguage) ?? "";
      return translatedTopicA.localeCompare(translatedTopicB);
    });
  }, [topicTags, consult.consultLanguage]);

  const isSelectedThemeMatchesConsult = useMemo(
    () => allSessionThemes?.some((theme) => theme.key === consult.themeKey),
    [consult.themeKey, allSessionThemes],
  );
  const topicsToSuggest = useMemo(
    () =>
      isSelectedThemeMatchesConsult && sortedTopics.length > 0
        ? sortedTopics
        : [],
    [isSelectedThemeMatchesConsult, sortedTopics],
  );

  const notes: ConsultNotesVM = useMemo(
    () => ({
      consultId: consult.id,
      theme: allSessionThemes?.find((theme) => theme.key === consult.themeKey),
      topics: sortedTopics?.filter((topic) =>
        consultTopics?.some((selected) => selected.name === topic),
      ),
      reasonBook:
        consult.dossierNotes?.find(
          (n) => n.question === SESSION_NOTES_SECTIONS.REASON_BOOK,
        )?.answer ?? "",
      sessionDiscussed:
        consult.dossierNotes?.find(
          (n) => n.question === SESSION_NOTES_SECTIONS.SESSION_DISCUSSED,
        )?.answer ?? "",
      helpNeeded: consult.dossierNotes?.find(
        (n) => n.question === SESSION_NOTES_SECTIONS.HELP_NEEDED,
      )?.answer,
      provider: consult.dossierNotes?.find(
        (n) => n.question === SESSION_NOTES_SECTIONS.PROVIDER,
      )?.answer,
      providerName: consult.dossierNotes?.find(
        (n) => n.question === SESSION_NOTES_SECTIONS.PROVIDER_NAME,
      )?.answer,
      nextSteps: consult.dossierNotes?.find(
        (n) => n.question === SESSION_NOTES_SECTIONS.NEXT_STEPS,
      )?.answer,
      caOtherDescription:
        consult.dossierNotes?.find(
          (n) => n.question === SESSION_NOTES_SECTIONS.CA_OTHER_DESCRIPTION,
        )?.answer ?? "",
    }),
    [allSessionThemes, consult, consultTopics, sortedTopics],
  );

  useEffect(() => {
    const incoming = notes.topics ?? [];
    // only update state if the contents really changed
    if (
      incoming.length !== selectedTopics?.length ||
      incoming.some((tc, i) => tc !== selectedTopics[i])
    ) {
      setSelectedTopics(incoming);
    }
  }, [notes.topics]);

  const emptyState = useMemo(
    () => <span className="text-slate-600">{t("SessionNotesPaper.None")}</span>,
    [t],
  );

  // FLAGS: health-data, silent-failure
  // Pulls an AI summary of the session transcript and writes theme and topics straight to the record
  // before the operator has read any of it. Nothing here is wrapped: a rejection leaves
  // `isTranscriptionSummaryLoading` true, so the button spins for the rest of the page's life with no
  // message, and a partial run can leave the saved theme and topics disagreeing with the narrative.
  const loadTranscriptionSummary = async () => {
    setIsTranscriptionSummaryLoading(true);
    const response = await getTranscriptionSummary(consult.id);

    setEditingSection(NoteSections.SESSION_DISCUSSED);

    const matchedTheme = allSessionThemes.filter(
      (theme) =>
        response.theme?.toLocaleUpperCase() === theme.key?.toLocaleUpperCase(),
    );

    if (matchedTheme.length > 0) {
      notes.theme = matchedTheme[0];
      await updateConsultTheme({
        id: consult.id,
        themeKey: matchedTheme[0].key!,
      });
    }
    const topicsFromAI = topicTags?.filter((topicName) =>
      response.topics?.includes(topicName),
    );
    setSelectedTopics(topicsFromAI);
    await saveTags({
      tags: topicsFromAI?.map((tag) => tag) ?? [],
      id: consult.id,
    });

    sessionDiscussedRef.current?.setMarkdown(response.sessionNotes ?? "");

    appInsights.trackEvent({
      name: "ExpertsFlow",
      properties: {
        flow: "session-notes",
        eventType: "generate-with-ai",
        entityId: consult.id.toString(),
      },
    });
    setIsTranscriptionSummaryLoading(false);
  };

  const filteredAndSortedThemes = useMemo(
    () =>
      allSessionThemes?.toSorted((a, b) =>
        (
          getTranslatedThemeNameInSessionLanguage(
            a.key!,
            consult.consultLanguage,
          ) ?? ""
        ).localeCompare(
          getTranslatedThemeNameInSessionLanguage(
            b.key,
            consult.consultLanguage,
          ) ?? "",
        ),
      ),
    [allSessionThemes, consult.consultLanguage],
  );

  const networkProviders = useMemo(() => getOpenUpNetworkProviders(t), [t]);

  const helpNeededOptions = useMemo(
    () =>
      consult.consultType === SESSION_TYPE.FINANCIAL_WELLBEING
        ? getFinancialWellbeingHelpNeededOptions(t)
        : getHelpNeededOptions(t),
    [consult.consultType, t],
  );

  const getNetworkProviderText = () => {
    if (!notes.provider) return emptyState;
    return (
      networkProviders.find((p) => p.value === notes.provider)?.label ??
      notes.provider
    );
  };

  const helpNeededText = useMemo(() => {
    if (!notes.helpNeeded) {
      return emptyState;
    }

    if (notes.helpNeeded === HELP_NEEDED_VALUES.NO_REFER_TO_IPRACTICE) {
      const networkLabel =
        helpNeededOptions.find(
          (option) =>
            option.value === HELP_NEEDED_VALUES.REFER_TO_OPENUP_NETWORK,
        )?.label ??
        t("Consults.SessionDetails.HelpClient.referToOpenUpNetwork");
      const iPracticeLabel = t(
        "Consults.SessionDetails.HelpClient.NetworkProvider.iPractice",
      );
      return `${networkLabel} - ${iPracticeLabel}`;
    }
    if (notes.helpNeeded === HELP_NEEDED_VALUES.NO_CLIENT_AT_RISK) {
      return (
        helpNeededOptions.find(
          (option) => option.value === HELP_NEEDED_VALUES.OTHER,
        )?.label ?? t("Consults.SessionDetails.HelpClient.otherReason")
      );
    }

    return (
      helpNeededOptions.find((option) => option.value === notes.helpNeeded)
        ?.label ?? notes.helpNeeded
    );
  }, [notes.helpNeeded, helpNeededOptions, t, emptyState]);

  const getNextStepsText = () => {
    const nextSteps = getNextStepsForSessionNotes(t);
    return (
      nextSteps.find((s) => s.value === notes.nextSteps)?.label ?? emptyState
    );
  };

  const { mutateAsync: updateOrCreateNote, isPending: updateNoteSubmiting } =
    useConsultNotesMutation(consult.user.id!, consult.id);
  const { mutateAsync: deleteNotes } = useDeleteSessionNoteMutation(
    consult.user.id!,
    consult.id,
  );
  const reasonBookRef = useRef<MDXEditorMethods>(null);

  const handleSectionChange = useCallback(
    (section: NoteSections | undefined) => {
      if (
        editingSection === NoteSections.SESSION_DISCUSSED ||
        editingSection === NoteSections.REASON_BOOK
      )
        return;
      setEditingSection(section);
    },
    [editingSection],
  );

  const deleteDossierNoteIfExists = useCallback(
    async (questions: string[]) => {
      const existingNotes = consult.dossierNotes?.filter(
        (n) => n.question != null && questions.includes(n.question),
      );
      if (existingNotes && existingNotes.length > 0) {
        await deleteNotes(questions);
      }
    },
    [consult.dossierNotes, deleteNotes],
  );

  const handleHelpNeededSave = useCallback(
    async (newVal: string) => {
      notes.helpNeeded = newVal;
      await updateOrCreateNote({
        question: SESSION_NOTES_SECTIONS.HELP_NEEDED,
        answer: newVal,
      });

      if (newVal === HELP_NEEDED_VALUES.REFER_TO_OPENUP_NETWORK) {
        notes.providerName = undefined;
        await deleteDossierNoteIfExists([SESSION_NOTES_SECTIONS.PROVIDER_NAME]);
      } else {
        notes.provider = undefined;
        notes.providerName = undefined;
        await deleteDossierNoteIfExists([
          SESSION_NOTES_SECTIONS.PROVIDER,
          SESSION_NOTES_SECTIONS.PROVIDER_NAME,
        ]);
      }
    },
    [notes, updateOrCreateNote, deleteDossierNoteIfExists],
  );

  const handleProviderSave = useCallback(
    async (newVal: string) => {
      notes.provider = newVal;
      await updateOrCreateNote({
        question: SESSION_NOTES_SECTIONS.PROVIDER,
        answer: newVal,
      });
      if (newVal !== PROVIDER_VALUES.OTHER) {
        await deleteDossierNoteIfExists([SESSION_NOTES_SECTIONS.PROVIDER_NAME]);
      }
    },
    [notes, updateOrCreateNote, deleteDossierNoteIfExists],
  );

  const renderProviderNameField = (
    testId: string,
    placeholder: string,
    required: boolean,
  ) => (
    <>
      {editingSection !== NoteSections.PROVIDER_NAME ? (
        <EditableDisplay
          data-testid={testId}
          className={CLICKABLE_FIELD_CLASS}
          onActivate={() => handleSectionChange(NoteSections.PROVIDER_NAME)}
        >
          {notes.providerName || emptyState}
        </EditableDisplay>
      ) : (
        <TextField
          autoFocus
          required={required}
          variant="standard"
          defaultValue={notes.providerName || ""}
          placeholder={placeholder}
          className="w-full"
          onBlur={async (e) => {
            const newValue = e.target.value;
            if (required && !newValue.trim()) return;
            notes.providerName = newValue;
            await updateOrCreateNote({
              question: SESSION_NOTES_SECTIONS.PROVIDER_NAME,
              answer: newValue,
            });
            handleSectionChange(undefined);
          }}
          onKeyDown={async (e) => {
            if (e.key === "Enter") {
              const newValue = (e.target as HTMLInputElement).value;
              if (required && !newValue.trim()) return;
              notes.providerName = newValue;
              await updateOrCreateNote({
                question: SESSION_NOTES_SECTIONS.PROVIDER_NAME,
                answer: newValue,
              });
              handleSectionChange(undefined);
            }
            if (e.key === "Escape") {
              handleSectionChange(undefined);
            }
          }}
        />
      )}
    </>
  );

  const contextValue = useMemo(
    () => ({
      editingSection,
      handleSectionChange,
      closeEditing: () => setEditingSection(undefined),
    }),
    [editingSection, handleSectionChange],
  );

  const onCloseTopics = useCallback(async () => {
    await saveTags(
      {
        tags: selectedTopics ?? [],
        id: consult.id,
      },
      {
        onError: () => {
          snackbarShowMessage("Failed to update topics", "error");
        },
      },
    );
    if (
      isConfidentialAdvisorSessionType &&
      !selectedTopics?.includes(TOPIC_NAMES.CONFIDENTIAL_ADVISOR_OTHER)
    ) {
      await deleteDossierNoteIfExists([
        SESSION_NOTES_SECTIONS.CA_OTHER_DESCRIPTION,
      ]);
    }
    handleSectionChange(undefined);
  }, [
    selectedTopics,
    consult.id,
    isConfidentialAdvisorSessionType,
    saveTags,
    handleSectionChange,
    snackbarShowMessage,
    deleteDossierNoteIfExists,
  ]);

  // FLAGS: silent-failure
  // The `.catch` is empty by design comment, so a rejected save of the free-text "other topics" note
  // leaves the typed text on screen as though it were stored.
  const onBlurOtherTopics: NonNullable<TextFieldProps["onBlur"]> = useCallback(
    (e) => {
      updateOrCreateNote({
        question: SESSION_NOTES_SECTIONS.CA_OTHER_DESCRIPTION,
        answer: e.target.value,
      })
        .then(() => handleSectionChange(undefined))
        .catch(() => {
          // do nothing
        });
    },
    [updateOrCreateNote, handleSectionChange],
  );

  return (
    <EditingContext.Provider value={contextValue}>
      <div
        className="p-6 w-full md:mb-4 bg-white rounded-lg shadow-md flex flex-col"
        data-cy="session-notes"
      >
        <div className="grid grid-cols-[1fr_auto] gap-6 items-start">
          <div className="flex flex-col">
            <div className="sub1 mb-4">Session Informations</div>
            {consultIsLoading ? (
              <div className="flex w-full p-8 justify-center">
                <Spinner />
              </div>
            ) : (
              <div className="flex flex-col">
                {/* Theme */}
                <SessionNoteRow label="Theme of session">
                  {editingSection !== NoteSections.THEME ? (
                    <EditableDisplay
                      className={CLICKABLE_FIELD_CLASS}
                      onActivate={() => handleSectionChange(NoteSections.THEME)}
                    >
                      {getTranslatedThemeNameInSessionLanguage(
                        notes.theme?.key ?? "",
                        consult.consultLanguage,
                      ) ?? emptyState}
                    </EditableDisplay>
                  ) : (
                    <Autocomplete
                      openOnFocus
                      autoFocus
                      disablePortal
                      options={filteredAndSortedThemes}
                      getOptionLabel={(opt) =>
                        getTranslatedThemeNameInSessionLanguage(
                          opt.key,
                          consult.consultLanguage,
                        ) ?? ""
                      }
                      value={notes.theme ?? null}
                      className="w-full"
                      onChange={async (_e, newVal) => {
                        notes.theme = newVal ?? undefined;
                        if (newVal)
                          await updateConsultTheme(
                            {
                              id: consult.id,
                              themeKey: newVal.key!,
                            },
                            {
                              onError: () => {
                                snackbarShowMessage(
                                  "Failed to update theme",
                                  "error",
                                );
                              },
                            },
                          );
                        handleSectionChange(undefined);
                      }}
                      renderInput={(params) => (
                        <TextField autoFocus variant="standard" {...params} />
                      )}
                    />
                  )}
                </SessionNoteRow>

                {/* Topics */}
                <SessionNoteRow label="Topics covered">
                  {editingSection !== NoteSections.TOPICS ? (
                    <EditableDisplay
                      className={CLICKABLE_FIELD_CLASS}
                      onActivate={() =>
                        handleSectionChange(NoteSections.TOPICS)
                      }
                    >
                      {selectedTopics && selectedTopics.length > 0
                        ? selectedTopics
                            ?.map((topic) =>
                              getTranslatedTopicName(
                                topic,
                                consult.consultLanguage,
                              ),
                            )
                            .join(", ")
                        : emptyState}
                    </EditableDisplay>
                  ) : (
                    <Autocomplete
                      openOnFocus
                      disablePortal
                      multiple
                      disableCloseOnSelect
                      options={topicsToSuggest}
                      getOptionLabel={(opt) =>
                        getTranslatedTopicName(opt, consult.consultLanguage) ??
                        ""
                      }
                      value={selectedTopics ?? []}
                      className="w-full"
                      onChange={(_e, newVal, reason) => {
                        if (
                          ["selectOption", "removeOption", "clear"].includes(
                            reason,
                          )
                        ) {
                          setSelectedTopics(newVal);
                        }
                      }}
                      onClose={onCloseTopics}
                      renderOption={(props, option, { selected }) => (
                        <li {...props}>
                          <Checkbox
                            icon={<CheckBoxOutlineBlankIcon className="mr-3" />}
                            checkedIcon={<CheckBoxIcon className="mr-3" />}
                            className="text-indigo-800 body1"
                            checked={selected}
                          />
                          {getTranslatedTopicName(
                            option,
                            consult.consultLanguage,
                          )}
                        </li>
                      )}
                      renderTags={(tags) =>
                        tags.map((tag, idx) => (
                          <p key={tag} className="pl-1">
                            {getTranslatedTopicName(
                              tag,
                              consult.consultLanguage,
                            )}
                            {idx < tags.length - 1 && ","}
                          </p>
                        ))
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          autoFocus
                          label={t("Consults.SessionDetails.SelectTopics")}
                          variant="standard"
                        />
                      )}
                    />
                  )}
                </SessionNoteRow>

                {/* CA Other Description */}
                {selectedTopics?.includes(
                  TOPIC_NAMES.CONFIDENTIAL_ADVISOR_OTHER,
                ) && (
                  <SessionNoteRow label="Other Topics">
                    {editingSection === NoteSections.CA_OTHER_DESCRIPTION ? (
                      <TextField
                        autoFocus
                        variant="standard"
                        className="w-full"
                        defaultValue={notes.caOtherDescription}
                        onBlur={onBlurOtherTopics}
                      />
                    ) : (
                      <EditableDisplay
                        className={CLICKABLE_FIELD_CLASS}
                        onActivate={() =>
                          handleSectionChange(NoteSections.CA_OTHER_DESCRIPTION)
                        }
                      >
                        {notes.caOtherDescription || emptyState}
                      </EditableDisplay>
                    )}
                  </SessionNoteRow>
                )}

                {/* Referred */}
                <SessionNoteRow label="Referred">
                  <EditableSelectField<string>
                    data-testid="helpNeeded-select"
                    section={NoteSections.HELP_NEEDED}
                    currentValue={notes.helpNeeded}
                    displayValue={helpNeededText}
                    options={helpNeededOptions.map((option) => ({
                      label: option.label,
                      value: option.value,
                    }))}
                    onSave={handleHelpNeededSave}
                  />
                </SessionNoteRow>

                {/* OpenUp Network provider sub-dropdown */}
                {notes.helpNeeded ===
                  HELP_NEEDED_VALUES.REFER_TO_OPENUP_NETWORK && (
                  <SessionNoteRow label="OpenUp Network provider">
                    <EditableSelectField<string>
                      data-testid="network-provider-select"
                      section={NoteSections.PROVIDER}
                      currentValue={notes.provider}
                      displayValue={getNetworkProviderText()}
                      options={networkProviders.map((p) => ({
                        label: p.label,
                        value: p.value,
                      }))}
                      onSave={handleProviderSave}
                    />
                  </SessionNoteRow>
                )}

                {/* Outside OpenUp network: mandatory provider name */}
                {notes.helpNeeded ===
                  HELP_NEEDED_VALUES.NO_REFER_TO_OTHER_PROVIDER && (
                  <SessionNoteRow label="Provider name">
                    {renderProviderNameField(
                      "outside-provider-name-field",
                      "Enter provider name",
                      true,
                    )}
                  </SessionNoteRow>
                )}

                {/* Other: mandatory reason */}
                {notes.helpNeeded === HELP_NEEDED_VALUES.OTHER && (
                  <SessionNoteRow label="Reason">
                    {renderProviderNameField(
                      "other-reason-field",
                      "Enter reason",
                      true,
                    )}
                  </SessionNoteRow>
                )}

                {/* Follow-up */}
                <SessionNoteRow label="Follow-up">
                  <EditableSelectField<string>
                    section={NoteSections.NEXT_STEPS}
                    currentValue={notes.nextSteps}
                    displayValue={getNextStepsText()}
                    options={getNextStepsForSessionNotes(t).map((step) => ({
                      label: step.label,
                      value: step.value,
                    }))}
                    onSave={async (newVal) => {
                      notes.nextSteps = newVal;
                      await updateOrCreateNote({
                        question: SESSION_NOTES_SECTIONS.NEXT_STEPS,
                        answer: newVal,
                      });
                    }}
                  />
                </SessionNoteRow>
              </div>
            )}
          </div>

          {/* Right column: AI button or info box */}
          <div className="shrink-0">
            {consult.hasTranscription && (
              <div className="flex gap-2 p-2">
                <Button
                  loading={isTranscriptionSummaryLoading}
                  onClick={loadTranscriptionSummary}
                  className="!px-4 !py-1"
                >
                  Autofill with AI
                </Button>
              </div>
            )}
            {!consult.employee.isTranscriptionAllowed && (
              <div className="flex items-start gap-3 p-4 text-left bg-slate-100 rounded-md border border-solid border-slate-200">
                <CircleMinus className="text-slate-600 shrink-0" size={18} />
                <div className="flex flex-col">
                  <div className="text-sm font-medium pb-1 text-slate-800">
                    AI Session Notes is disabled for your account.
                  </div>
                  <div className="text-xs font-medium text-slate-600">
                    Session audio is not being transcribed.
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {!consultIsLoading && (
          <>
            {/* Background of session */}
            <div className="flex flex-col mt-4 mb-10">
              <div className="sub1 m-1">Background of session</div>
              <EditableMarkdownSection
                section={NoteSections.REASON_BOOK}
                currentValue={notes.reasonBook}
                editorRef={reasonBookRef}
                onSave={async (content) => {
                  notes.reasonBook = content;
                  await updateOrCreateNote({
                    question: SESSION_NOTES_SECTIONS.REASON_BOOK,
                    answer: content,
                  });
                }}
                isSubmitting={updateNoteSubmiting}
              />
            </div>

            {/* Discussed during the session */}
            <div className="flex flex-col mt-4 mb-10">
              <div className="sub1 m-1">Discussed during the session</div>
              <EditableMarkdownSection
                section={NoteSections.SESSION_DISCUSSED}
                currentValue={notes.sessionDiscussed}
                editorRef={sessionDiscussedRef}
                onSave={async (content) => {
                  notes.sessionDiscussed = content;
                  await updateOrCreateNote({
                    question: SESSION_NOTES_SECTIONS.SESSION_DISCUSSED,
                    answer: content,
                  });
                }}
                isSubmitting={updateNoteSubmiting}
              />
            </div>

            {/* Attachments */}
            <div className="flex flex-col mt-4 mb-10">
              <AttachmentsBox consultId={consult.id} />
            </div>
          </>
        )}
      </div>
    </EditingContext.Provider>
  );
};

export default withSnackbar(SessionNotesPaper);
